import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type FileRejection } from '@mantine/dropzone';
import clsx from 'clsx';

import { fileSizeFormatter } from '../../utils/fileSizeFormatter';
import getFileExtensions, {
  getFileNameAndExtensionFromName,
} from '../../utils/getFileExtensions';
import { mapMimeTypeToSVGIconName } from '../../utils/mapMimeTypeToSVGIconName';
import { portalFilenamify } from '../../utils/portalFilenamify';
import { Button } from '../buttons/Button';
import { GoogleFontIcon } from '../icons/GoogleFontIcon';
import { SVGIcon } from '../icons/SVGIcon';
import { Input } from '../input/Input';
import { LoadingIndicator } from '../loading-indicator/LoadingIndicator';
import { TextWithTooltip } from '../tooltip/TextWithTooltip';
import { Tooltip } from '../tooltip/Tooltip';

import { type FileSelection, UpdateFileNamePayload } from './fileUploadState';
import { FileData, isFileSelection } from './uploadTypes';

import styles from './upload-file.module.scss';

type FileNameRendererProps = {
  fileData: FileSelection;
  isNameEditable?: boolean;
  onFileNameChange?(value: UpdateFileNamePayload): void;
};

// Input wrapper that maintains fileName before commiting to the reducer-state to aviod uncessary renders.
// This functional component also sanitizes filename onCommit.
function EditableFileName({
  fileData,
  onFileNameChange,
}: FileNameRendererProps) {
  const [fileName, setFileName] = useState(
    () => getFileNameAndExtensionFromName(fileData.file.name).originalFileName
  );

  useEffect(() => {
    setFileName(
      getFileNameAndExtensionFromName(fileData.file.name).originalFileName
    );
  }, [fileData.file.name]);

  return (
    <Input
      loading={['isFetchingMetadata', 'isPending'].includes(
        fileData.uploadState
      )}
      onCommit={(value) => {
        const sanitizedFileName = portalFilenamify(value);
        onFileNameChange?.({
          id: fileData.id,
          name: sanitizedFileName,
          cleared: value.trim().length === 0,
        });
        setFileName(sanitizedFileName);
      }}
      onChange={setFileName}
      rightSection={
        <span className={styles['file-extention']}>
          {fileData.originalFileExtention}
        </span>
      }
      withTooltip
      value={fileName}
      classNames={{ root: styles['input-size'] }}
    />
  );
}

function FileNameRenderer({
  fileData,
  isNameEditable = false,
  onFileNameChange,
}: FileNameRendererProps) {
  return (
    <div>
      {/* adding dummy file name label to aling the input box with json Editor's metadata input */}
      {/* this temp fix is to be removed when implementing a new ui */}
      {isNameEditable && (
        <label className={styles['file-name-label-ghost']}>File Name</label>
      )}
      {isNameEditable ? (
        <div className={clsx(styles['section'], styles['section-type-input'])}>
          <SVGIcon name={mapMimeTypeToSVGIconName(fileData.file.type)} />
          <EditableFileName
            fileData={fileData}
            onFileNameChange={onFileNameChange}
          />
        </div>
      ) : (
        <div className={styles['section']}>
          <SVGIcon name={mapMimeTypeToSVGIconName(fileData.file.type)} />
          <TextWithTooltip
            textProps={{
              className: styles['file-name'],
              lineClamp: 1,
            }}
            tooltipProps={{ usePortal: false }}
          >
            {fileData.file.name}
          </TextWithTooltip>
        </div>
      )}
    </div>
  );
}
type FileRejectionProps = {
  fileData: FileRejection;
  onRemoveFile?: (file: FileData) => void;
};

function FileRejection({ fileData, onRemoveFile }: FileRejectionProps) {
  const errorMessage = (fileData.errors[0]?.message ?? '')
    .replace(
      /\b\S+\/\S+\b/g,
      // Replace all mentions of MIME types with their corresponding file extensions
      (mime) => {
        const extensions = getFileExtensions([mime]);
        return extensions.length > 0 ? extensions.join(', ') : mime;
      }
    )
    .replace(
      /(\d+)\s*bytes/g,
      (_, bytes) => fileSizeFormatter(Number(bytes)) // Convert Bytes to MB
    );

  return (
    <div className={styles['divider']}>
      <div className={clsx(styles['root'], styles['error'])}>
        <div className={styles['section']}>
          <Tooltip label={errorMessage}>
            <GoogleFontIcon className={styles['error']} name="cancel" solid />
          </Tooltip>
          <TextWithTooltip
            textProps={{
              className: clsx(styles['file-name'], styles['rejected']),
              lineClamp: 1,
            }}
            tooltipProps={{ usePortal: false }}
          >
            {fileData.file.name}
          </TextWithTooltip>
        </div>

        <div className={styles['section']}>
          <div className={styles['file-size']}>
            {fileSizeFormatter(fileData.file.size)}
          </div>

          {onRemoveFile && (
            <div className={styles['actions']}>
              <Button
                variant="text"
                icon={<SVGIcon name="delete" />}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFile(fileData);
                }}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

type FileSelectionProps = {
  fileData: FileSelection;
  isNameEditable?: boolean;
  metaDataExtractionInput?: (fileData: FileSelection) => React.ReactNode;
  onFileNameChange?: (value: UpdateFileNamePayload) => void;
  onRemoveFile?: (file: FileData) => void;
};

function FileSelection({
  isNameEditable,
  fileData,
  metaDataExtractionInput,
  onFileNameChange,
  onRemoveFile,
}: FileSelectionProps) {
  return (
    <div
      className={clsx(styles['root'], {
        [styles['root-with-meta-data']]: metaDataExtractionInput,
      })}
    >
      <FileNameRenderer
        fileData={fileData}
        isNameEditable={isNameEditable}
        onFileNameChange={onFileNameChange}
      />

      {metaDataExtractionInput && (
        <div
          className={styles['section']}
          style={{ justifyContent: 'space-between' }}
        >
          {metaDataExtractionInput(fileData)}
        </div>
      )}

      <div className={styles['section']}>
        <div className={styles['file-size']}>
          {fileSizeFormatter(fileData.file.size)}
        </div>

        {onRemoveFile && (
          <div className={styles['actions']}>
            <Button
              variant="text"
              icon={<SVGIcon name="delete" />}
              onClick={(e) => {
                e.stopPropagation();
                onRemoveFile(fileData);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export type Props = {
  fileData: FileData;
  isNameEditable?: boolean;
  metaDataExtractionInput?: (fileData: FileSelection) => React.ReactNode;
  onFileNameChange?: (value: UpdateFileNamePayload) => void;
  onRemoveFile?: (file: FileData) => void;
  uploadFile?: (file: FileSelection) => Promise<void>;
};

export function UploadFile({
  fileData,
  isNameEditable,
  metaDataExtractionInput,
  onFileNameChange,
  onRemoveFile,
  uploadFile,
}: Props) {
  const { t } = useTranslation();

  const isRejected = !isFileSelection(fileData);
  if (isRejected) {
    return <FileRejection fileData={fileData} onRemoveFile={onRemoveFile} />;
  }

  if (fileData.uploadState === 'initial') {
    return (
      <FileSelection
        fileData={fileData}
        isNameEditable={isNameEditable}
        metaDataExtractionInput={metaDataExtractionInput}
        onFileNameChange={onFileNameChange}
        onRemoveFile={onRemoveFile}
      />
    );
  }

  return (
    <div
      className={clsx(styles['root'], {
        [styles['root-with-meta-data']]: !!metaDataExtractionInput,
      })}
    >
      <FileNameRenderer
        fileData={fileData}
        onFileNameChange={onFileNameChange}
        isNameEditable={isNameEditable}
      />

      {metaDataExtractionInput && (
        <div
          className={styles['section']}
          style={{ justifyContent: 'space-between' }}
        >
          {metaDataExtractionInput(fileData)}
        </div>
      )}

      <div className={styles['section']}>
        {fileData.uploadState === 'isError' && (
          <div className={styles['error-text']}>
            {t('app.upload.uploadFailed')}
          </div>
        )}
        <div className={styles['file-size']}>
          {fileSizeFormatter(fileData.file.size)}
        </div>
        <div className={styles['actions']}>
          {fileData.uploadState === 'isSuccess' && (
            <GoogleFontIcon
              name="check_circle"
              solid
              className={styles['success']}
            />
          )}
          {['isPending', 'isFetchingMetadata'].includes(
            fileData.uploadState
          ) && (
            <LoadingIndicator
              className={styles['pending']}
              data-testid="loading-indicator"
            />
          )}
          {fileData.uploadState === 'isError' && (
            <>
              {uploadFile && (
                <Button
                  variant="text"
                  icon={<SVGIcon name="retry" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    uploadFile?.(fileData);
                  }}
                />
              )}
              {onRemoveFile && (
                <Button
                  variant="text"
                  icon={<SVGIcon name="delete" />}
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveFile(fileData);
                  }}
                />
              )}
              <Tooltip label={fileData.error}>
                <GoogleFontIcon
                  className={styles['error']}
                  name="cancel"
                  solid
                />
              </Tooltip>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
